import { TimeUtils } from "@/utils";
import { AppConstants } from "@/common";
import { GuideConstants, type ProgramGuideItem, type Channel, type GuideProgramsSegment } from "@ca.vmedia.tv/domains";
import { type GuideSize, type ChannelGuide, type ExposeInterface, FilterTabsType } from "./types";
import { Services } from "@ca.vmedia.tv/services";
import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
import { useChannelsStore } from "@/stores/Channels";
import { useRoute } from 'vue-router';
import { useApplicationStore } from "@/stores/Application";
import { Strings } from "@/locales/types/enums/strings";
import { useUserInfoStore } from "@/stores/UserInfo";

export const useGuideStore = defineStore('Guide', (): ExposeInterface => {
    const { $i18n, $domains } = useNuxtApp();

    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const ApplicationStore = useApplicationStore();
    const { 
        setTooltipeShowing, 
        setTooltipeMessage
    } = ApplicationStore;

    const route = useRoute();
    const { guideConfig } = useAppConfig();

    const ChannelsStoreModule = useChannelsStore();
    const {
        getAvailableChannels,
        getNotAvailableChannels,
        getOutOfHomeChannels
    } = storeToRefs(ChannelsStoreModule);

    const FavoriteChannelsStoreModule = useFavoriteChannelsStore();
    const {
        getFavChannelsIds: favoriteChannels,
    } = storeToRefs(FavoriteChannelsStoreModule); 

    const _timeMarkInterval: number = 30 * 60 * 1000; // 30 min to ms
    const _channelWidthDefault: number = 239;
    const _segmentDuration: number = GuideConstants.PROGRAMS_PAGE_SIZE * 60 * 60 * 1000; // 4 hours
    const _now = TimeUtils.dateNow();
    const _currentTime = _now - (_now % _timeMarkInterval);
    const _sizeHalfHourPx = AppConstants.sizeHalfHourPx;

    const _guideSize: Ref<GuideSize> = ref({
        width: 0,
        height: 0,
    });
    const _channelWidth: Ref<number> = ref(_channelWidthDefault);
    const _channelsViewPort: Ref<ChannelGuide[] | null> = ref(null);
    const _fromTimeSegments: Ref<number[]> = ref([]);
    const _focusedProgram: Ref<ProgramGuideItem | null> = ref(null);
    const _configProgram: Ref<number[]> = ref([0, _now]);
    const _viewportBeginTimeMs: Ref<number> = ref(_currentTime);
    const _timeMsPerPixel: Ref<number> = ref(_timeMarkInterval / _sizeHalfHourPx); // 30 min / 424 px
    const _gapWidth: Ref<number> = ref(4);

    const _isFromRoutePlayer: Ref<boolean> = ref(false);
    const getIsFromRoutePlayer = computed((): boolean => _isFromRoutePlayer.value);
    const setIsFromRoutePlayer = (value: boolean): void => {
        _isFromRoutePlayer.value = value;
    };

    const timeMsPerPixel = computed((): number => _timeMsPerPixel.value);
    const viewportTotalTime = computed((): number => Math.floor((_guideSize.value.width - _channelWidth.value) * _timeMsPerPixel.value));
    const viewportEndTime = computed((): number => _viewportBeginTimeMs.value + viewportTotalTime.value);
    const timelineBarTimes = computed((): number[] => {
        const begin = _viewportBeginTimeMs.value;
        const residue = _viewportBeginTimeMs.value % _timeMarkInterval;
        const times: number[] = [];
        let time = begin - residue;

        for (let i = 0; i < 4; i++) {
            times.push(time);
            time += _timeMarkInterval;
        }

        return times;
    });
    const viewportStartTime = computed((): number => timelineBarTimes.value[0]);

    const defaultFilterParams = computed((): FilterTabsType => !isGuest.value && $domains.lsCacher.get<boolean | null>('isOutOfHome') === true ? FilterTabsType.OUT_OF_HOME : FilterTabsType.ALL);
    const _filterParams: Ref<FilterTabsType> = ref(defaultFilterParams.value);
    const filterParams = computed((): FilterTabsType => _filterParams.value);
    const setFilterParams = (id: FilterTabsType): void => {
        if(id === filterParams.value) {
            return;
        }
        
        _filterParams.value = id;
        localStorage.setItem("guideFilter", id.toString());
    };
    const resetGuideFilter = (): void => {
        setFilterParams(defaultFilterParams.value);
    };
    const initGuideFilter = () => {
        const cacheFilterGuide = JSON.parse(localStorage.getItem("guideFilter")!);

        if (cacheFilterGuide) {
            setFilterParams(cacheFilterGuide);
        } else {
            resetGuideFilter();
        }
    };


    const timeMarkInterval = computed((): number => _timeMarkInterval);
    const channelsViewPort = computed((): ChannelGuide[] | null => _channelsViewPort.value);
    const channelsViewPortIsEmpty = computed((): boolean => _channelsViewPort.value !== null && _channelsViewPort.value.length === 0);
    const channelsViewPortIsNull = computed((): boolean => _channelsViewPort.value === null);
    
    const _filtered_channels: Ref<Channel[]> = ref([]);
    const guideFilteredChannels = computed((): Channel[] => _filtered_channels.value);

    const getFromTimeSegments = computed((): number[] => _fromTimeSegments.value);

    /**
     * Getter to deliver programs for channels
     * @param {number[]} channelIds channel ids
     * @param {number[]} fromTimeSegments time segments
     * @returns {Promise<{[channelId: string]: ProgramGuideItem[]}>} programs
     */
    const _getPrograms = (channelIds: number[], fromTimeSegments: number[]): Promise<{[channelId: string]: ProgramGuideItem[]}> => {
        return new Promise((resolve, reject) => {
            const programsMerge: { [channelId: string]: ProgramGuideItem[] } = {};
            const programs: {[channelId: string]: ProgramGuideItem[]} = {};
            let programsCount = fromTimeSegments.length;
            
            fromTimeSegments.forEach((fromTimeSegment: number) => {
                const segmentDatas: Promise<GuideProgramsSegment> = Services
                    .Instance
                    .guide
                    .getSegmentFromCache(channelIds, fromTimeSegment);

                segmentDatas.then((segmentData: GuideProgramsSegment) => {
                    if (Object.keys(segmentData).length !== 0) {
                        for (const channelId in segmentData) {
                            if (programsMerge[channelId]) {
                                if (segmentData[channelId]) {
                                    programsMerge[channelId] = [...programsMerge[channelId], ...Object.values(segmentData[channelId])];
                                }
                            } else if (segmentData[channelId]) {
                                programsMerge[channelId] = Object.values(segmentData[channelId]);
                            }
                        }

                        let unique: ProgramGuideItem[] = [];
                        for (const channelId in programsMerge) {
                            unique = programsMerge[channelId].reduce<ProgramGuideItem[]>((accumulator, current) => {
                                const filter = (item: ProgramGuideItem) =>
                                    current.recentAirTime !== null
                                    && item.id === current.id
                                    && item.recentAirTime?.startTime === current.recentAirTime.startTime;
        
                                if (!accumulator.some(filter)) {
                                    accumulator.push(current);
                                }
                                return accumulator;
                            }, []); 

                            programs[channelId] = unique;
                        }
                        // wait for all programs
                        if (--programsCount == 0){
                            for (const keyPrograms in programs) {
                                programs[keyPrograms]
                                .sort((program1: ProgramGuideItem, program2: ProgramGuideItem) => (program1.recentAirTime?.startTime ?? 0) - (program2.recentAirTime?.startTime ?? 0));
                            }
                            resolve(programs);
                        }
                    }
                }).catch((error) => {
                    reject(error); 
                });
            });
            
        });
    };

    /**
     * Getter to deliver Slice of available channels, taking into account pagination
     * @param {number | null} channelId channel id
     * @returns {Promise<ChannelGuide[]>} slice channels
     */
    const _sliceChannels = (channelId: number | null, fetchProgramsFlag: boolean): Promise<ChannelGuide[]> => {
        return new Promise((resolve, reject) => {
            if (!channelId) {
                reject(new Error('channelId is null or undefined'));
                return;
            }
            const channelsLoadingSize = guideConfig.channelsPageSize;
            const targetChannels = guideFilteredChannels.value.length > 0 ? guideFilteredChannels.value : getAvailableChannels.value;
            const channelIndex = targetChannels.findIndex((item: Channel) => item.id === channelId);
            const pageCount = Math.floor(channelIndex / channelsLoadingSize);
            let start = 0;
            let end = 0;

            if (pageCount === 0) {
                end = channelsLoadingSize;
            } else {
                start = pageCount * channelsLoadingSize;
                end = start + channelsLoadingSize;
            }

            const slicedChannels = targetChannels.slice(start, end);
            const channelIds: number[] = slicedChannels.map((channel: Channel) => channel.id!);

            if (channelIds.length !== 0) {
                const channels: ChannelGuide[] = [];

                if (fetchProgramsFlag) {
                    _getPrograms(channelIds, getFromTimeSegments.value).then((programs: {[channelId: string]: ProgramGuideItem[]}) => {
                        slicedChannels.forEach((channel: Channel) => {
                            if (channel.id) {
                                channels.push({
                                    channel: channel,
                                    items: programs[channel.id]
                                });
                            }
                        });
                        resolve(channels);
                    }).catch((e) => {
                        reject(e);
                    });
                } else {
                    slicedChannels.forEach((channel: Channel) => {
                        if (channel.id) {
                            channels.push({
                                channel: channel,
                                items: []
                            });
                        }
                    });
                    resolve(channels);
                }
            } else {
                resolve([]);
            }
        });
    };
    const gapGuideGrid = computed((): number => _gapWidth.value);
    const focusedProgram = computed((): ProgramGuideItem | null => {
        if (_focusedProgram.value) {
            //for existing program
            return _focusedProgram.value;
        } else {
            //for default program
            const guide = _channelsViewPort.value;
            if (guide === undefined || guide === null || guide.length === 0) {
                return null;
            }

            const [channelIdx, starttime] = _configProgram.value;
            if (!guide[channelIdx] || !guide[channelIdx].items) {
                return null;
            };
            const targetChannel = [...guide[channelIdx].items].reverse();
            const targetProgram = targetChannel.find((program) => program.recentAirTime?.startTime != null ? program.recentAirTime?.startTime <= starttime : null);
            return targetProgram ?? null;
        }
    });
    const configProgram = computed((): number[] => _configProgram.value);
    const channelWidth = computed((): number => _channelWidth.value);
    const channelsIdsGuide = computed((): number[] => {
        const guide = channelsViewPort.value;

        if (guide === null) {
            return [];
        }

        const channelIds: number[] = [];

        guide.forEach((element: ChannelGuide) => {
            if (element.channel.id) {
                channelIds.push(element.channel.id);
            }
        });

        return channelIds;
    });
    const focusedChannel = computed((): Channel | null => {
        const [channelIdx] = configProgram.value;

        if (channelsViewPort.value === undefined || channelsViewPort.value === null || channelsViewPort.value.length === 0) {
            return null;
        }
        return channelsViewPort.value[channelIdx]?.channel ?? null;
    });
    const pendingLoading = computed((): boolean => {
        return _channelsViewPort.value === null || (_channelsViewPort.value.length !== 0 && _channelsViewPort.value[0] && _channelsViewPort.value[0].items.length === 0); 
    });

    const fetchFilteredChannels = (): void => {
        switch (filterParams.value) {
            case FilterTabsType.SUBSCRIBED:
                _filtered_channels.value = getAvailableChannels.value;
                break;
            case FilterTabsType.FAVOURITES:
                _filtered_channels.value = getAvailableChannels.value.filter((item: Channel) => favoriteChannels.value.includes(item.id!));
                break;
            case FilterTabsType.OUT_OF_HOME:
                _filtered_channels.value = getOutOfHomeChannels.value;
                break;
            default:
                _filtered_channels.value = getNotAvailableChannels.value;
        }
    };
    const setState = (): void => {
        const {channelId, channelIdx} = route.query;

        if (channelId !== undefined && channelIdx !== undefined) {
            const channelIdxInt = parseInt(channelIdx as string);
            const time = TimeUtils.dateNow();
            const viewportBeginTime = time - (time % _timeMarkInterval);
            setViewportBeginTime(viewportBeginTime);
            setCoordProgram([channelIdxInt, time]);
        }
    };
    const initState = (): void => {
        const time = TimeUtils.dateNow();
        const viewportBeginTime = time - (time % _timeMarkInterval);
        
        setDefaultCoordProgram();
        _setDefaultFromTimeSegment(time);
        setViewportBeginTime(viewportBeginTime);
    };
    const fetchChannelsSegments = (_fetchProgramsFlag: boolean): void => {
        if (guideFilteredChannels.value.length === 0) {
            _setNextChannelsProgram([]);
            _setChannelsViewPort([]);
            return;
        }

        const channelId: number = route.query.channelId ? parseInt(route.query.channelId as string) : guideFilteredChannels.value[0].id!;
        if (channelId) {
            _sliceChannels(channelId, _fetchProgramsFlag).then((channels: ChannelGuide[]) => {
                _setChannelsViewPort(channels);
            }).catch((error) => {
                setTooltipeMessage($i18n.t(Strings.GUIDE_ERRORE_CHANNELS_SEGMENTS).toString());
                setTooltipeShowing({visible: true, showingTime: 5000});
                console.error("Error fetching channels segments: ", error);
            });
        }
    };
    const fetchPrevChannelsSegments = (_fetchProgramsFlag: boolean, _loop: boolean): void => {
        try {
            const {featureUpdateViewportChannelsEnabled} = guideConfig;

            if (guideFilteredChannels.value.length === 0) {
                _setNextChannelsProgram([]);
                return;
            }

            if (channelsViewPort.value === null || guideFilteredChannels.value.length === channelsViewPort.value.length || channelsViewPort.value.length === 0) {
                return;
            }
            
            const firstChannelId = channelsViewPort.value[0].channel.id;

            const channelIndex = guideFilteredChannels.value.findIndex((item: Channel) => item.id === firstChannelId);
            let targetChannelIndex = channelIndex - 1;

            if (targetChannelIndex < 0) {
                targetChannelIndex = _loop ? guideFilteredChannels.value.length - 1 : 0;
            }

            const targetChannelId = guideFilteredChannels.value[targetChannelIndex].id;

            if (targetChannelId !== null && channelsIdsGuide.value.includes(targetChannelId)) {
                return;
            }

            _sliceChannels(targetChannelId, _fetchProgramsFlag).then((channels: ChannelGuide[]) => {
                if (featureUpdateViewportChannelsEnabled) {
                    _setChannelsViewPort(channels);
                } else {
                    _setPrevChannelsViewport(channels);
                }
            }).catch((error) => {
                setTooltipeMessage($i18n.t(Strings.GUIDE_ERRORE_CHANNELS_SEGMENTS).toString());
                setTooltipeShowing({visible: true, showingTime: 5000});
               throw new Error("Error fetching previous channels segments: " + error);
            });
        } catch (error) {
            console.error(error);
        }
    };
    const fetchNextChannelsSegments = (_fetchProgramsFlag: boolean, _loop: boolean): void => {
        try {
            const {featureUpdateViewportChannelsEnabled} = guideConfig;

            if (guideFilteredChannels.value.length === 0) {
                _setNextChannelsProgram([]);
                return;
            }


            if (channelsViewPort.value === null || guideFilteredChannels.value.length === channelsViewPort.value.length || channelsViewPort.value.length === 0) {
                return;
            }

            const lastChannelId = channelsViewPort.value[channelsViewPort.value.length - 1].channel.id;
            const channelIndex = guideFilteredChannels.value.findIndex((item: Channel) => item.id === lastChannelId);
            let targetChannelIndex = channelIndex + 1;

            if (targetChannelIndex > guideFilteredChannels.value.length - 1) {
                targetChannelIndex = _loop ? 0 : guideFilteredChannels.value.length - 1;
            }

            const targetChannelId = guideFilteredChannels.value[targetChannelIndex].id;

            if (targetChannelId !== null && channelsIdsGuide.value.includes(targetChannelId)) {
                return;
            }
            
            _sliceChannels(targetChannelId, _fetchProgramsFlag).then((channels: ChannelGuide[]) => {
               if (featureUpdateViewportChannelsEnabled) {
                    _setChannelsViewPort(channels);
                } else {
                    _setNextChannelsProgram(channels);
                }
            }).catch((error) => {
                setTooltipeMessage($i18n.t(Strings.GUIDE_ERRORE_CHANNELS_SEGMENTS).toString());
                setTooltipeShowing({visible: true, showingTime: 5000});
                throw new Error("Error fetching next channels segments: " + error);
            });
        } catch (error) {
            console.error(error);
        }
    };
    const updatePrograms = (): void => {
        _updateFromTimeSegments();
        deletePrograms();
        _getPrograms(channelsIdsGuide.value, getFromTimeSegments.value).then((programs: {[channelId: string]: ProgramGuideItem[]}) => {
            _mergePrograms({ programs });
        }).catch((error) => {
            console.error(error);
        });
    };
    const addOffsetFocusedProgram = (payload: { program: ProgramGuideItem }): void => {
        const programInFocus = payload.program;
        const focusedProgramTime = programInFocus && programInFocus.recentAirTime && programInFocus.recentAirTime.startTime ? programInFocus.recentAirTime.startTime : null;

        if (focusedProgramTime && (focusedProgramTime < viewportStartTime.value || focusedProgramTime > viewportEndTime.value)) {
            setViewportBeginTime(focusedProgramTime);
        }
    };
    const addOffsetFirstFocusedProgram = (payload: {
        channelIdx: number;
        program: ProgramGuideItem;
    }): void => {
        const programInFocus = payload.program;
        const startTime = programInFocus && programInFocus.recentAirTime && programInFocus.recentAirTime.startTime ? programInFocus.recentAirTime.startTime : null;

        if (startTime === null) {
            return;
        }

        setViewportBeginTime(startTime);
        setCoordProgram([payload.channelIdx, startTime]);
    };
    const addOffsetLastFocusedProgram = (payload: {
        channelIdx: number;
        program: ProgramGuideItem;
    }): void => {
        const programInFocus = payload.program;
        const focusedProgramTime = programInFocus && programInFocus.recentAirTime && programInFocus.recentAirTime.startTime ? programInFocus.recentAirTime.startTime : null;

        if (focusedProgramTime === null) {
            return;
        }

        setViewportBeginTime(focusedProgramTime);
    };

    const setViewportBeginTime = (time: number): void => {
        _viewportBeginTimeMs.value = time;
    };
    const setCoordProgram = (position: number[]): void => {
        _configProgram.value = position;
    };
    const setDefaultCoordProgram = (): void => {
        _configProgram.value = [0, TimeUtils.dateNow()];
    };
    const _setDefaultFromTimeSegment = (_time: number): void => {
        const featurePrevProgramEnabled = guideConfig.featurePrevProgramEnabled;
        const timeSpanHs = GuideConstants.PROGRAMS_PAGE_SIZE;
        const dateFrom = new Date(_time);
        dateFrom.setHours(Math.floor(dateFrom.getHours() / timeSpanHs) * timeSpanHs, 0, 0, 0);
        const time = dateFrom.getTime();

        if (featurePrevProgramEnabled) {
            _fromTimeSegments.value = [time - _segmentDuration, time, time + _segmentDuration];
        } else {
            _fromTimeSegments.value = [time, time + _segmentDuration];
        }
    };
    /**
     * Clone channel guide object
     * @param {ChannelGuide} channelGuide channel guide object
     * @returns {ChannelGuide} cloned channel guide object
     */
    const _cloneChannelGuide = (channelGuide: ChannelGuide): ChannelGuide => ({
        channel: { ...channelGuide.channel },
        items: [...channelGuide.items],
    });
    /**
     * Merge channels by id
     * @param {ChannelGuide[]} current current channels
     * @param {ChannelGuide[]} incoming incoming channels
     * @returns {ChannelGuide[]} merged channels
     */
    const _mergeChannelsById = (current: ChannelGuide[], incoming: ChannelGuide[]): ChannelGuide[] => {
        const map = new Map<number, ChannelGuide>();

        for (const ch of current) {
            if (ch.channel.id != null) {
                map.set(ch.channel.id, _cloneChannelGuide(ch));
            } else {
                console.error("Channel id is null", JSON.stringify({
                    name: ch.channel.name,
                    accessNum: ch.channel.accessNum,
                    callSign: ch.channel.callSign,
                }));
            }
        }

        for (const ch of incoming) {
            if (ch.channel.id != null) {
                map.set(ch.channel.id, _cloneChannelGuide(ch)); // replace if exists, or add new
            } else {
                console.error("Channel id is null", JSON.stringify({
                    name: ch.channel.name,
                    accessNum: ch.channel.accessNum,
                    callSign: ch.channel.callSign,
                }));
            }
        }

        return Array.from(map.values());
    }
    /**
     * Set next channels program in viewport
     * @param {ChannelGuide[]} _channels channels to set
     */
    const _setNextChannelsProgram = (_channels: ChannelGuide[]): void => {
        _channelsViewPort.value = _mergeChannelsById(
            _channelsViewPort.value ?? [],
            _channels
        );
    };
    /**
     * Set channels viewport
     * @param {ChannelGuide[]} channels channels to set
     */
    const _setChannelsViewPort = (channels: ChannelGuide[]): void => {
        _channelsViewPort.value = channels.map(_cloneChannelGuide);
    };
    /**
     * Set previous channels program in viewport
     * @param {ChannelGuide[]} _channels channels to set
     */
    const _setPrevChannelsViewport = (_channels: ChannelGuide[]): void => {
        if (_channelsViewPort.value === null) {
            console.error("Channels viewport is null, cannot set prev channels program.");
            return;
        }
        _channelsViewPort.value = _mergeChannelsById(
            _channels,
            _channelsViewPort.value,
        );
    };
    const _updateFromTimeSegments = (): void => {
        const featurePrevProgramEnabled = guideConfig.featurePrevProgramEnabled;
        const timeSpanHs = GuideConstants.PROGRAMS_PAGE_SIZE;
        const dateFrom = new Date(viewportStartTime.value);
        dateFrom.setHours(Math.floor(dateFrom.getHours() / timeSpanHs) * timeSpanHs, 0, 0, 0);
        const time = dateFrom.getTime();

        if (featurePrevProgramEnabled) {
            _fromTimeSegments.value = [time - _segmentDuration, time, time + _segmentDuration];
        } else {
            _fromTimeSegments.value = [time, time + _segmentDuration];
        }
    };
    /**
     * Delete all programs in channels viewport
     */
    const deletePrograms = (): void => {
        if (_channelsViewPort.value === null) {
            return;
        }

        _channelsViewPort.value = _channelsViewPort.value.map(channelGuide => ({
            ...channelGuide,
            items: [],
        }));
    };
    /**
     * Merge programs in channels viewport
     * @param {Object} payload payload with programs
     */
    const _mergePrograms = (payload: { programs: {[channelId: string]: ProgramGuideItem[]} }): void => {
        if (_channelsViewPort.value === null) {
            return;
        }

        _channelsViewPort.value = _channelsViewPort.value.map(channelGuide => {
            const id = channelGuide.channel.id;
            const newItems = id !== null && payload.programs[id]
                ? [...payload.programs[id]]
                : channelGuide.items;

            return {
                ...channelGuide,
                items: newItems,
            };
        });
    };
    const setGuideSize = (size: GuideSize): void => {
        _guideSize.value = size;
    };
    const setReducedSizeConfig = (): void => {
        const defaultInnerWidth = 1920;
        const ratioWidth = window.innerWidth / defaultInnerWidth;

        _channelWidth.value = _channelWidthDefault * ratioWidth;
        _timeMsPerPixel.value = _timeMarkInterval / ((window.innerWidth * _sizeHalfHourPx) / defaultInnerWidth);
    };
    const clearChannelsViewport = (): void => {
        _channelsViewPort.value = null;
    };
    const setFocusedProgram = (program: ProgramGuideItem | null): void => {
        _focusedProgram.value = program;
    };
    const clearFocusedProgram = (): void => {
        _focusedProgram.value = null;
    };
    const clearAll = (): void => {
        _guideSize.value = { width: 0, height: 0 };
        _channelWidth.value = _channelWidthDefault;
        _channelsViewPort.value = null;
        _filtered_channels.value = [];
        _fromTimeSegments.value = [];
        clearFocusedProgram();
        _configProgram.value = [0, TimeUtils.dateNow()];
        _viewportBeginTimeMs.value = _currentTime;
        _timeMsPerPixel.value = _timeMarkInterval / _sizeHalfHourPx;
        _gapWidth.value = 4;
    };

    return {
        timeMsPerPixel,
        viewportTotalTime,
        timelineBarTimes,
        viewportStartTime,
        timeMarkInterval,
        channelsViewPort,
        channelsViewPortIsEmpty,
        channelsViewPortIsNull,
        guideFilteredChannels,
        gapGuideGrid,
        focusedProgram,
        configProgram,
        channelWidth,
        channelsIdsGuide,
        focusedChannel,
        pendingLoading,
        fetchFilteredChannels,
        setState,
        initState,
        fetchChannelsSegments,
        fetchNextChannelsSegments,
        fetchPrevChannelsSegments,
        updatePrograms,
        addOffsetFocusedProgram,
        addOffsetFirstFocusedProgram,
        addOffsetLastFocusedProgram,
        setViewportBeginTime,
        setCoordProgram,
        setDefaultCoordProgram,
        deletePrograms,
        setGuideSize,
        setReducedSizeConfig,
        clearChannelsViewport,
        setFocusedProgram,
        clearFocusedProgram,
        clearAll,
        getIsFromRoutePlayer,
        setIsFromRoutePlayer,
        filterParams,
        setFilterParams,
        resetGuideFilter,
        initGuideFilter
    }
});